/**
 * Utility for detecting WebP browser support
 */
export class WebPDetector {
    private static supportPromise: Promise<boolean> | null = null;
    private static cachedSupport: boolean | null = null;

    /**
     * Checks browser WebP support
     * Uses caching for fast repeated calls
     */
    public static async isSupported(): Promise<boolean> {
        // If already checked - return cache
        if (this.cachedSupport !== null) {
            return this.cachedSupport;
        }

        // If check is already running - return existing promise
        if (this.supportPromise) {
            return this.supportPromise;
        }

        // Start new check
        this.supportPromise = this.detectWebPSupport();
        this.cachedSupport = await this.supportPromise;
        
        return this.cachedSupport;
    }

    /**
     * Synchronous check (returns null if not checked yet)
     */
    public static isSupportedSync(): boolean | null {
        return this.cachedSupport;
    }

    /**
     * Performs asynchronous WebP detection
     */
    private static detectWebPSupport(): Promise<boolean> {
        return new Promise((resolve) => {
            // Create test WebP image (1x1 transparent pixel)
            const webpData = 'data:image/webp;base64,UklGRiQAAABXRUJQVlA4IBgAAAAwAQCdASoBAAEAAwA0JaQAA3AA/vuUAAA=';
            
            const img = new Image();
            
            img.onload = () => {
                // If image loaded and has dimensions - WebP is supported
                const supported = img.width > 0 && img.height > 0;
                console.log(`🖼️ WebP support: ${supported ? '✅ Yes' : '❌ No'}`);
                resolve(supported);
            };
            
            img.onerror = () => {
                console.log('🖼️ WebP support: ❌ No');
                resolve(false);
            };
            
            img.src = webpData;
        });
    }

    /**
     * Get file extension depending on support
     */
    public static getExtension(): 'webp' | 'png' {
        return this.cachedSupport ? 'webp' : 'png';
    }

    /**
     * Get MIME type depending on support
     */
    public static getMimeType(): string {
        return this.cachedSupport ? 'image/webp' : 'image/png';
    }
}

